/**
 * Manages the inactivity auto-logout.
 *
 * Two timers run in tandem :
 *  1. `warningTimer` fires `inactivityWarningMinutes` *before* the timeout
 *     and runs the registered onWarn() callback (the AppShell shows a
 *     SweetAlert "extend session ?").
 *  2. `logoutTimer` fires at the timeout itself and runs onTimeout().
 *
 * `pingActivity()` resets both. `setConfig()` re-arms with new minutes.
 *
 * Defaults (used until /platformSettings/public has loaded) match the
 * pre-existing behaviour : 30 min hard cap, 1 min warning.
 */

let timeoutMin = 30;
let warningMin = 1;
let onWarn = () => {};
let onTimeout = () => {};

let warningTimer = null;
let logoutTimer = null;
let armed = false;

const clearTimers = () => {
  if (warningTimer) {
    window.clearTimeout(warningTimer);
    warningTimer = null;
  }
  if (logoutTimer) {
    window.clearTimeout(logoutTimer);
    logoutTimer = null;
  }
};

const armTimers = () => {
  clearTimers();
  if (!armed) return;
  const timeoutMs = timeoutMin * 60 * 1000;
  const warningOffset = Math.max(0, (timeoutMin - warningMin) * 60 * 1000);
  if (warningMin > 0 && warningOffset < timeoutMs) {
    warningTimer = window.setTimeout(() => {
      try { onWarn(); } catch (_e) {}
    }, warningOffset);
  }
  logoutTimer = window.setTimeout(() => {
    try { onTimeout(); } catch (_e) {}
  }, timeoutMs);
};

export const startSessionTimeout = ({ onWarn: w, onTimeout: t }) => {
  if (typeof w === 'function') onWarn = w;
  if (typeof t === 'function') onTimeout = t;
  armed = true;
  armTimers();
};

export const stopSessionTimeout = () => {
  armed = false;
  clearTimers();
};

export const pingSessionActivity = () => {
  if (armed) armTimers();
};

export const setSessionTimeoutConfig = ({ inactivityTimeoutMinutes, inactivityWarningMinutes }) => {
  if (Number.isFinite(inactivityTimeoutMinutes) && inactivityTimeoutMinutes > 0) {
    timeoutMin = inactivityTimeoutMinutes;
  }
  if (Number.isFinite(inactivityWarningMinutes) && inactivityWarningMinutes >= 0) {
    warningMin = inactivityWarningMinutes;
  }
  if (armed) armTimers();
};

export const getSessionTimeoutConfig = () => ({
  inactivityTimeoutMinutes: timeoutMin,
  inactivityWarningMinutes: warningMin,
});
